// Bring in a design made somewhere else: ask Claude (or a designer, or another email tool) for an HTML email,
// paste it or upload the file, and Courage cleans it, checks it, and sends it with the footer and unsubscribe
// link under it. The body of an email or a sequence step is either the builder's blocks or one imported design.
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cleanHtml, designOf, htmlToText, warnings, MERGE_TAGS, MAX_DESIGN, type Block } from "@couragemfg/api/email-render";
import { BlockEditor } from "./BlockEditor";
import { uid } from "./emApi";

/** What to tell Claude so the design comes back ready to send. */
export const CLAUDE_BRIEF = `Design an HTML email for: [what it's for, who it's going to, the one thing they should do]

Make it work in Outlook and Gmail:
- A table-based layout 600px wide, with inline styles (no flex or grid)
- No scripts, forms or embedded video
- Every image as an https:// link (nothing saved inside the HTML)
- A real https:// link on every button

For personal touches use ${MERGE_TAGS.filter((t) => ["first_name", "company", "owner_name"].includes(t.tag)).map((t) => t.example).join(", ")} where they fit.
Leave out the footer, postal address and unsubscribe link: they're added automatically.

Give me the complete HTML file.`;

const kb = (s: string) => Math.max(1, Math.round(new TextEncoder().encode(s).length / 1024));

/** Size, links and images of a design, for a one-line summary. */
export function designStats(html: string) {
  const links = (html.match(/<a\b[^>]*\shref\s*=/gi) ?? []).length;
  const images = (html.match(/<img\b/gi) ?? []).length;
  return { kb: kb(html), links, images, full: /<body\b/i.test(html) };
}
const statLine = (s: ReturnType<typeof designStats>) =>
  `${s.full ? "A full email" : "Part of an email"} · ${s.kb} KB · ${s.links} link${s.links === 1 ? "" : "s"} · ${s.images} image${s.images === 1 ? "" : "s"}`;

export function CopyBrief() {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(CLAUDE_BRIEF); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* the text is right there to select */ }
  };
  return (
    <details className="em-brief">
      <summary>Making it in Claude? Start with these instructions</summary>
      <pre>{CLAUDE_BRIEF}</pre>
      <button type="button" className="btn" onClick={copy}>{copied ? "Copied" : "Copy the instructions"}</button>
    </details>
  );
}

/** Paste or choose a file; shows what Courage will take out and what it found. */
export function ImportFields({ value, onChange, autoFocus }: { value: string; onChange: (html: string) => void; autoFocus?: boolean }) {
  const [file, setFile] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const cleaned = useMemo(() => (value.trim() ? cleanHtml(value) : null), [value]);
  const pick = async (f: File | undefined) => {
    setErr(null);
    if (!f) return;
    if (f.size > MAX_DESIGN) { setErr(`${f.name} is ${Math.round(f.size / 1024)} KB. Designs can be up to 500 KB, and Gmail cuts off emails over about 100 KB.`); return; }
    const text = await f.text();
    if (!/<[a-z!][\s\S]*>/i.test(text)) { setErr(`${f.name} doesn't look like HTML.`); return; }
    setFile(f.name); onChange(text);
  };
  return (
    <div className="em-import">
      <div className="field">
        <label htmlFor="imp-html">Paste the HTML</label>
        <textarea id="imp-html" className="textarea mono em-code" rows={9} value={value} autoFocus={autoFocus} spellCheck={false}
          onChange={(e) => { setFile(null); onChange(e.target.value); }} placeholder={"<!doctype html>\n<html>…</html>"} />
        <div className="em-import-file">
          <span className="muted">or</span>
          <label className="btn">Choose an .html file<input type="file" accept=".html,.htm,text/html" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} /></label>
          {file && <span className="muted">{file}</span>}
        </div>
        {err && <p className="em-import-err" role="alert">{err}</p>}
      </div>
      {cleaned && (
        <p className="em-import-sum">
          {statLine(designStats(cleaned.html))}
          {cleaned.title ? <> · “{cleaned.title}”</> : null}
          {cleaned.removed.length > 0 && <><br /><span className="muted">Courage takes out {cleaned.removed.join(", ")}: inboxes block them.</span></>}
        </p>)}
      <CopyBrief />
    </div>
  );
}

function ImportDialog({ replacing, onClose, onImport }: { replacing: boolean; onClose: () => void; onImport: (html: string, title: string | null) => void }) {
  const [html, setHtml] = useState("");
  useEffect(() => {
    // Capture first, so Escape closes this and not the drawer it opened from.
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    window.addEventListener("keydown", k, true); return () => window.removeEventListener("keydown", k, true);
  }, [onClose]);
  const go = () => { const c = cleanHtml(html); onImport(c.html, c.title); };
  return createPortal(
    <div className="peek-wrap mk-drawer" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="peek em-dialog em-import-dialog" role="dialog" aria-modal="true" aria-labelledby="imp-h">
        <div className="peek-h"><div><h2 id="imp-h">Import a design</h2><div className="peek-sub">{replacing ? "It replaces what's in this email now." : "From Claude, a designer, or another email tool."}</div></div>
          <button type="button" className="icon-btn" aria-label="Close" onClick={onClose}><svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.6" /></svg></button></div>
        <div className="peek-body"><ImportFields value={html} onChange={setHtml} autoFocus /></div>
        <div className="peek-actions"><button className="btn primary" disabled={!html.trim()} onClick={go}>Import</button><button type="button" className="btn" onClick={onClose}>Cancel</button></div>
      </div>
    </div>,
    document.body,
  );
}

const hasContent = (blocks: Block[]) => blocks.some((b) => ("text" in b && b.text.trim() && !/^Hi \{\{first_name\|there\}\},?\s*$/.test(b.text.trim())) || (b.type === "image" && b.src) || b.type === "html");

/** The body of an email: the block builder, or an imported design. */
export function BodyEditor({ blocks, onChange, disabled, onTitle, name, heading }: {
  blocks: Block[]; onChange: (b: Block[]) => void; disabled?: boolean; onTitle?: (t: string) => void; name?: string; heading?: ReactNode;
}) {
  const [importing, setImporting] = useState(false);
  const design = designOf(blocks);
  const doImport = (html: string, title: string | null) => {
    onChange([{ id: uid(), type: "html", html }]);
    if (title) onTitle?.(title);
    setImporting(false);
  };
  return (
    <>
      <div className="em-body-h">
        {heading ?? <h2 className="em-h2">Content</h2>}
        {!disabled && !design && <button type="button" className="linkish" onClick={() => setImporting(true)}>Import HTML instead</button>}
      </div>
      {design
        ? <DesignPanel html={design.html} name={name} disabled={disabled} onChange={(html) => onChange([{ ...design, html }])} onReplace={() => setImporting(true)}
            onBuilder={() => {
              if (!confirm("Go back to the builder? The imported design is removed; its words come along as text.")) return;
              onChange([{ id: uid(), type: "text", text: htmlToText(design.html) }, { id: uid(), type: "signature" }]);
            }} />
        : <BlockEditor blocks={blocks} onChange={onChange} disabled={disabled} />}
      {importing && <ImportDialog replacing={hasContent(blocks)} onClose={() => setImporting(false)} onImport={doImport} />}
    </>
  );
}

function DesignPanel({ html, name, disabled, onChange, onReplace, onBuilder }: {
  html: string; name?: string; disabled?: boolean; onChange: (html: string) => void; onReplace: () => void; onBuilder: () => void;
}) {
  const [code, setCode] = useState(false);
  const stats = designStats(html);
  const notes = warnings({ blocks: [{ id: "x", type: "html", html }] });
  const download = () => {
    const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    const a = document.createElement("a");
    a.href = url; a.download = `${(name || "email").replace(/[^\w.-]+/g, "-").replace(/^-+|-+$/g, "") || "email"}.html`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <div className="em-block em-design">
      <div className="em-block-h"><span className="t">Imported design</span></div>
      <div className="em-block-b">
        <p className="em-design-sum">{statLine(stats)}</p>
        <p className="muted em-note">Courage fills in merge tags like {"{{first_name|there}}"}, tags the links for the campaign, and adds the footer with your postal address and unsubscribe link under it.</p>
        {notes.length > 0 && <ul className="em-design-warn" aria-label="Worth a look">{notes.map((w) => <li key={w}>{w}</li>)}</ul>}
        <div className="em-design-acts">
          {!disabled && <button type="button" className="btn" onClick={onReplace}>Replace</button>}
          <button type="button" className="btn" onClick={() => setCode(!code)} aria-expanded={code}>{code ? "Hide the HTML" : disabled ? "See the HTML" : "Edit the HTML"}</button>
          <button type="button" className="btn" onClick={download}>Download</button>
          {!disabled && <button type="button" className="linkish" onClick={onBuilder}>Use the builder instead</button>}
        </div>
        {code && <textarea className="textarea mono em-code" aria-label="The design's HTML" rows={18} value={html} readOnly={disabled} spellCheck={false} onChange={(e) => onChange(e.target.value)} />}
        {!disabled && <p className="hint em-design-tip">To change the design, download it, ask Claude for the change, and replace it here.</p>}
      </div>
    </div>
  );
}
